import {useEffect, useMemo, useRef, useState} from 'react'

import {ActorRowList} from './ActorRowList'
import {ActorSelectPanel} from './ActorSelectPanel'
import {ALL_CATEGORIES_SENTINEL, CategoryFilter} from './CategoryFilter'
import {type Actor, actorKey, type ActorListEditorProps} from './types'

/**
 * Generic editor for a list of actors. Renders a header with title and
 * (optionally) a category segmented filter + dropdown selector, followed by
 * a list of the currently-selected actors. Caller supplies async data sources
 * for suggestions and categories, and `onAdd` / `onRemove` callbacks invoked
 * when the user toggles items in the selector dropdown or deletes a row.
 */
export function ActorListEditor<TActor extends Actor>({
  title,
  subtitle,
  readOnly,
  actors,
  selector,
  baseAvatarUrl,
  categories,
  onAdd,
  onRemove,
  renderRowActions,
  emptyState,
}: ActorListEditorProps<TActor>) {
  const [categoryFilter, setCategoryFilter] = useState<string>(ALL_CATEGORIES_SENTINEL)
  // `null` means "no authoritative category list available" — either the
  // caller didn't provide `fetchCategories`, the request failed, or it
  // returned an empty/invalid response. The category filter is hidden in
  // that case and consumers fall back to actor-type labels.
  const [availableCategories, setAvailableCategories] = useState<string[] | null>(null)

  const fetchCategories = categories?.fetchCategories
  useEffect(() => {
    if (!fetchCategories) return
    let cancelled = false
    void (async () => {
      try {
        const result = await fetchCategories()
        if (cancelled) return
        setAvailableCategories(Array.isArray(result) && result.length > 0 ? result : null)
      } catch {
        if (!cancelled) setAvailableCategories(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [fetchCategories])

  const selectedIds = useMemo(() => {
    const set = new Set<string>()
    for (const a of actors) set.add(actorKey(a))
    return set
  }, [actors])

  const showCategoryFilter = !readOnly && (availableCategories?.length ?? 0) > 1

  const anchorRef = useRef<HTMLButtonElement>(null)

  const headerControls = (
    <div className="d-flex flex-wrap flex-items-center gap-2">
      {showCategoryFilter && availableCategories && (
        <CategoryFilter categories={availableCategories} value={categoryFilter} onChange={setCategoryFilter} />
      )}
      {!readOnly && (
        <ActorSelectPanel
          selector={selector}
          selectedActors={actors}
          selectedIds={selectedIds}
          baseAvatarUrl={baseAvatarUrl}
          categoryFilter={categoryFilter}
          categoriesAvailable={availableCategories !== null}
          onAdd={onAdd}
          onRemove={onRemove}
          anchorRef={anchorRef}
        />
      )}
    </div>
  )

  return (
    <div>
      {subtitle ? <div className="tmp-mb-3">{subtitle}</div> : null}
      <div className="Box">
        <div className="Box-header d-flex flex-wrap flex-justify-between flex-items-center gap-2 py-2">
          <h3 className="Box-title">{title}</h3>
          {headerControls}
        </div>
        <ActorRowList
          actors={actors}
          readOnly={readOnly}
          baseAvatarUrl={baseAvatarUrl}
          categoryFilter={categoryFilter}
          categoriesAvailable={availableCategories !== null}
          onRemove={onRemove}
          renderRowActions={renderRowActions}
          emptyState={emptyState}
        />
      </div>
    </div>
  )
}
